import { format as formatDate, isValid } from 'date-fns';
import type { TFunction } from 'i18next';
import { getColorValue } from '@/core/lib/color-contrast';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';
import { formatMinutes } from '@/features/tasks/public';
import type { FormFieldKey, TaskFieldOptions } from '../components/TaskFieldEditor';

/** One value on a chip: what it reads as, and the colour it carries in its own right. */
export interface TaskFieldChipPart {
  label: string;
  /** A CSS colour when the value has one — a status or a tag — else null. */
  color: string | null;
}

/**
 * What one field shows on its chip: the resolved label when the value names something the board
 * knows, the value itself otherwise, and null when there is nothing set.
 *
 * A list rather than a string because tags hold several values, each with its own colour — a
 * board identifies a status and a tag by colour, and rendering them all in one accent said
 * nothing about which was which.
 *
 * Shared by both quick-create tabs. They read the value from different places — a proposal draft
 * and a blank form — but a priority, a date or an assignee has to read the same either way.
 */
export function taskFieldChipValue(
  field: FormFieldKey,
  value: unknown,
  options: TaskFieldOptions,
  t: TFunction,
  dateFormat: string,
): TaskFieldChipPart[] | null {
  // Tags first: they are the one field holding several values, and collapsing to the first hid
  // every tag the proposal named after it.
  if (field === 'tags') {
    const entries = (Array.isArray(value) ? value : [value]).filter(Boolean);
    return entries.length > 0 ? entries.map((entry) => resolve(options.tags, entry)) : null;
  }

  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined || raw === '') return null;

  if (field === 'priority') return [plain(t(`quickCreate.priorities.${String(raw)}`))];
  if (field === 'estimatedMinutes') return [plain(formatMinutes(Number(raw)))];

  // The user's configured format, not the `yyyy-MM-dd` the draft stores: the chip is read, not
  // parsed, and the wire shape has no business showing up in it.
  if (field === 'dueDate' || field === 'startDate') {
    // `parseDatePickerValue` checks the shape, not the calendar, so "2026-13-45" reaches
    // date-fns as an Invalid Date and `format` throws on it — taking the whole popover to the
    // error boundary because the model wrote a plausible-looking date that does not exist.
    const parsed = parseDatePickerValue(String(raw));
    return [plain(isValid(parsed) ? formatDate(parsed, dateFormat) : String(raw))];
  }

  const list =
    field === 'assigneeId'
      ? options.members
      : field === 'statusId'
        ? options.statuses
        : options.groups;
  return [resolve(list, raw)];
}

/** A value with no colour of its own — a date, a priority, a person. */
function plain(label: string): TaskFieldChipPart {
  return { label, color: null };
}

/**
 * The option's label and colour, or the raw value when the board does not have it — which is what
 * a status or a tag the proposal is proposing to create looks like from here.
 */
function resolve(options: { id: string; label: string; color?: string | null }[], raw: unknown) {
  const match = options.find((option) => option.id === raw);
  return {
    label: match?.label ?? String(raw),
    color: match?.color ? getColorValue(match.color) || null : null,
  };
}
